<script setup lang="ts">
import GlobalSearchPopover from '@/components/core/GlobalSearchPopover.vue'
import DefaultAvatar from '@/components/core/DefaultAvatar.vue'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import Breadcrumb from '@/components/ui/Breadcrumb.vue'
import { Languages, LogOut, User, Sun, MoonStar, Menu, Download, Mail } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { computed } from 'vue'
import { useAppStore } from '@/stores/app'
import { useAccountStore } from '@/stores/accountStore'
import { useI18n } from 'vue-i18n'
import { setLocale } from '@/i18n'
import { SUPPORT_SUBJECT, supportMailto } from '@/lib/supportMail'

const store = useAppStore()
const accountStore = useAccountStore()
const { t, locale } = useI18n()

import { DOWNLOAD_URL } from '@/config/app'

const toggleMode = () => {
  store.toggleTheme()
}

const switchLanguage = (lang: 'enUS' | 'zhCN') => {
  if (locale.value !== lang) setLocale(lang)
}

const supportHref = computed(() =>
  supportMailto({
    subject: SUPPORT_SUBJECT,
    accountEmail: accountStore.account.email,
  }),
)

</script>

<template>
  <nav
    class="flex items-center justify-between h-[64px] border-b-[1px] px-4 fixed z-40 top-0 bg-background/80 backdrop-blur-lg border-b border-border"
    :style="{ width: store.navWidth }"
  >
    <div class="w-24 hidden lg:block">
      <Breadcrumb />
    </div>
    <div class="w-2/5 hidden lg:block">
      <GlobalSearchPopover />
    </div>
    <Button
      variant="outline"
      class="p-[6px] w-8 h-8 transition-all duration-200 block lg:hidden"
      :class="store.sidebarExpanded ? 'bg-transparent' : 'dark:bg-white'"
      @click="store.toggleSidebar()"
    >
      <Menu class="transition-all duration-500 text-black" />
    </Button>
    <div class="flex items-center">
      <Button
        variant="outline"
        class="border-0 p-[6px] ml-2 w-8 h-8"
        :aria-label="t('common.toggleTheme')"
        @click="toggleMode"
      >
        <Sun v-if="store.isDark" />
        <MoonStar v-else />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button
            variant="outline"
            class="border-0 p-[6px] ml-2 w-8 h-8"
            :aria-label="t('common.language')"
          >
            <Languages />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-32 relative mr-4">
          <DropdownMenuItem
            class="cursor-pointer"
            :class="{ 'bg-secondary': locale === 'enUS' }"
            @click="switchLanguage('enUS')"
          >
            English
          </DropdownMenuItem>
          <DropdownMenuItem
            class="cursor-pointer"
            :class="{ 'bg-secondary': locale === 'zhCN' }"
            @click="switchLanguage('zhCN')"
          >
            中文
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <a :href="DOWNLOAD_URL" target="_blank" rel="noopener noreferrer" class="ml-3">
        <Button
          variant="outline"
          class="h-8 px-3 text-sm font-semibold flex items-center gap-1.5"
        >
          <Download class="w-4 h-4" />
          {{ t('common.download') }}
        </Button>
      </a>
      <div class="border-x-[1px] border-border h-[24px] w-[1px] mx-2 ml-3"></div>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button
            variant="outline"
            class="border-0 flex items-center max-w-[200px] w-full justify-start"
          >
            <Avatar class="h-8 w-8">
              <AvatarImage
                v-if="accountStore.account.avatarUrl"
                :src="accountStore.account.avatarUrl"
                :alt="accountStore.account.username || 'User'"
              ></AvatarImage>
              <AvatarFallback><DefaultAvatar :seed="accountStore.account.username || accountStore.account.email" /></AvatarFallback>
            </Avatar>
            <span class="ml-2 hidden md:flex justify-start flex-col items-start">
              <p class="mb-0">{{ accountStore.account.username || 'User' }}</p>
              <small class="text-xs text-muted-foreground font-light">{{
                accountStore.account.email || ''
              }}</small>
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-56 relative mr-4">
          <DropdownMenuLabel>{{ accountStore.account.username || 'User' }}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem class="cursor-pointer" @click="$router.push('/home/user')">
            <User class="mr-2 h-4 w-4" />
            <span>{{ t('common.profile') }}</span>
          </DropdownMenuItem>
          <DropdownMenuItem as-child class="cursor-pointer">
            <a :href="supportHref" class="flex items-center">
              <Mail class="mr-2 h-4 w-4" />
              <span>{{ t('common.emailSupport') }}</span>
            </a>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem class="cursor-pointer" @click="accountStore.logout()">
            <LogOut class="mr-2 h-4 w-4" />
            <span>{{ t('common.logout') }}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </nav>
</template>
